// shellform.ts ↔ game/Shell.cpp:474-729 ShellForm (the shell-side modal form
// base: full-screen positioning, the zip-in control animation, the title-screen
// background) + the ShellEnv bundle — the C++ globals every shell form reaches
// (gpmfrmm/gpiniForms/gevm/gtimm/gsndm/gpprefs/gpakr + the ggame/gsim slices),
// injected once and threaded through each form's constructor.
//
// ShellForm lives apart from shell.ts so the form subclasses (gameoptions.ts,
// help.ts, missionpick.ts) can extend it without an import cycle through the
// shell flow.

import type { RectLike } from './rect.ts';
import type { MultiFormMgr } from '../render/paintengine.ts';
import type { EventMgr } from './event.ts';
import type { TimerMgr } from './timer.ts';
import type { PackFileReader } from '../io/packfile.ts';
import type { Preferences } from '../io/prefs.ts';
import type { ClientAnimationData } from '../gfx/animdata.ts';
import type { MissionIdentifier, SaveStore } from '../io/savegame.ts';
import type { Stream } from '../../../sim/src/sm/stream.ts';
// type-only (erased; no runtime import cycle) — the geometry-bearing pause-menu
// button descriptor the DOM in-game-menu seam threads to the view.
import type { InGameMenuButton } from './gameoptions.ts';
// type-only (erased; no runtime import cycle) — the win/lose summary payload the
// DOM summary seam threads to the view.
import type { ObjectivesSummaryInfo } from './objectives.ts';
// type-only (erased; no runtime import cycle) — the in-mission E-Com overlay payload
// the DOM ecom seam threads to the view (Form 6).
import type { EcomInfo } from './ecom.ts';
// type-only (erased; no runtime import cycle) — the in-mission cutscene overlay payload
// the DOM cutscene seam threads to the view (kidfCutScene 1047).
import type { CutSceneInfo } from './cutscene.ts';

// game/res.h:87-88 (kidcOk/kidcCancel live in form.ts)
export const kidcHelp = 2004;
export const kidcDefault = 2005;

// game/res.h:20
export const kidfStartup = 1021;

// game/res.h:150
export const kidcVersion = 1011;

/** gsndm — the SoundMgr slice the shell forms reach (game/soundmgr.cpp;
 *  audio/soundmgr.ts is the real implementation, this seam is its shape). */
export interface SoundMgr {
  /** gsndm.PlaySfx — takes the SimUIHost union (fingerhandler.ts:126): a plain
   *  Sfx id, or `sfxc:<n>` marking SfxFromCategory(n), the async-rng variant
   *  pick the C++ does at the call site (e.g. SimUI.cpp:2332); the real
   *  SoundMgr resolves it, MemorySoundMgr drops it like any other id. */
  PlaySfx(sfx: number | string): void;
  Enable(fEnable: boolean): void; // gsndm.Enable
  IsEnabled(): boolean; // gsndm.IsEnabled
  GetVolume(): number; // gsndm.GetVolume (0-255; <0 = no volume ctl)
  SetVolume(n: number): void; // gsndm.SetVolume
  SaveStateAndClear(): void; // gsndm.SaveStateAndClear (disableSoundEvent)
  RestoreState(): void; // gsndm.RestoreState (enableSoundEvent)
  /** glAsyncSeed adoption (audio/soundmgr.ts): a GameSession hands its sim's
   *  SimContext.rngAsync over so all async sfx picks ride ONE stream. The
   *  state-only managers omit it. */
  SetRngAsync?(rngAsync: { GetRandom(): number }): void;
}

/** The state-only default (tests/headless hosts — no audio output). */
export class MemorySoundMgr implements SoundMgr {
  private fEnabled = true;
  private nVolume = 100; // knPrefVolume default (preferences.cpp:115)
  PlaySfx(_sfx: number | string): void {}
  Enable(fEnable: boolean): void {
    this.fEnabled = fEnable;
  }
  IsEnabled(): boolean {
    return this.fEnabled;
  }
  GetVolume(): number {
    return this.nVolume;
  }
  SetVolume(n: number): void {
    this.nVolume = n;
  }
  SaveStateAndClear(): void {}
  RestoreState(): void {}
}

/** The option globals (game/game.cpp:50-62 + game/GameOptions.cpp:20-33).
 *  Sessions may implement fields as accessors over live storage (e.g.
 *  nScrollSpeed over the input slice's gnScrollSpeed). */
export interface OptionsState {
  fLassoSelection: boolean; // gfLassoSelection (game.cpp:51)
  fMouseMode: boolean; // gfMouseMode (game.cpp:52)
  nScrollSpeed: number; // gnScrollSpeed (game.cpp:53, float)
  wfHandicap: number; // gwfHandicap (game.cpp:62)
  nHueOffset: number; // gnHueOffset (display HSL adjust)
  nSatMultiplier: number; // gnSatMultiplier
  nLumOffset: number; // gnLumOffset
  wfPerfOptions: number; // gwfPerfOptions
  nDemoRank: number; // gnDemoRank
}

// the game.cpp:50-62 initializers (+ kfHcapDefault/kfPerfMax)
export function defaultOptionsState(): OptionsState {
  return {
    fLassoSelection: false,
    fMouseMode: false,
    nScrollSpeed: 1.0,
    wfHandicap: 0x0002 | 0x0040 | 0x0010 | 0x0020, // kfHcapDefault (ht.h:298)
    nHueOffset: 0,
    nSatMultiplier: 0,
    nLumOffset: 0,
    wfPerfOptions: 0xffff, // kfPerfMax (gwfPerfOptions init, game.cpp:57)
    nDemoRank: 0,
  };
}

/** DisplayOptionsForm's gpdisp/ggame mode reaches (GameOptions.cpp:726-830) —
 *  the web display has exactly one mode; these seams default accordingly. */
export interface DisplaySeam {
  GetModeMatchCount(): number; // ggame.GetModeMatchCount
  GetModeMatchCurrent(): number; // ggame.GetModeMatchCurrent
  GetModeMatchBest(): number; // ggame.GetModeMatchBest
  GetModeMatch(imm: number): { imode: number; nDepthData: number; nSizeData: number };
  GetModeInfo(imode: number): {
    cx: number;
    cy: number;
    nDepth: number;
    nDegreeOrientation: number;
  };
  RequestModeChange(imm: number): void; // ggame.RequestModeChange
  GetDepth(): number; // gpdisp->GetMode().nDepth
}

/** The C++ globals the shell-side forms and flows reach. */
export interface ShellEnv {
  mfrmm: MultiFormMgr; // gpmfrmm
  evm: EventMgr; // gevm
  gtimm: TimerMgr; // gtimm
  pakr: PackFileReader; // gpakr
  sndm: SoundMgr; // gsndm
  prefs: Preferences; // gpprefs
  savePreferences(): void; // ggame.SavePreferences
  options: OptionsState;
  GetPlayfieldSize(): { cx: number; cy: number }; // ggame.GetPlayfieldSize
  getSafeAreas(): RectLike; // gpdisp->GetSafeAreas

  // ---- in-game reaches (null while in the shell, no game running) ----
  fMultiplayer: boolean; // gfMultiplayer
  sim: { Pause(fPause: boolean): void } | null; // gsim
  getSimUIForm(): { SetUIType(): void } | null; // ggame.GetSimUIForm
  plrLocal: { SetHandicap(wf: number): void; GetFlags(): number } | null; // gpplrLocal
  gameSpeed(): number; // gtGameSpeed (the live value)
  setGameSpeed(t: number): void; // ggame.SetGameSpeed

  // ---- host/seam reaches ----
  /** LoadAnimationData (Help's <IMG> arm; game/Animation.cpp loader). */
  loadAnimationData(pszName: string): ClientAnimationData | null;
  /** HtMessageBox (game/gameform.cpp) — M4c modal; default logs. */
  msgBox?(pszTitle: string, pszText: string): void;
  /** HostAnalytics. */
  hostAnalytics?(psz: string): void;
  /** HostOpenUrl (Shell.cpp:42). */
  openUrl?(pszUrl: string): void;
  /** HostInitiateWebView (Shell.cpp:37). */
  initiateWebView?(pszTitle: string, pszUrl: string): void;
  /** ggame.SaveGame (InputUI.cpp:364) — modal through PickSaveGameStream
   *  (game.cpp:2438-2442); done = the C++ return point. */
  saveGame?(done?: () => void): void;
  /** PickLoadGameStream (game/loadsave.cpp:5-29 — the modal picker form,
   *  ui/loadsave.ts) — continuation-shaped: done(pstm|null). */
  pickLoadGameStream?(done: (pstm: Stream | null) => void): void;
  /** gpstmSavedGame = pstm (InputUI.cpp:380 — the knGoLoadSavedGame handoff). */
  setSavedGameStream?(pstm: Stream): void;
  /** gpstmSavedGame consumer (Shell.cpp:85 — the knGoLoadSavedGame reload). */
  takeSavedGameStream?(): Stream | null;
  /** ggame.AskResignGame (game.cpp:1864-1915) — the MP exit-confirm modal +
   *  CsPlayerResign; done(fYes) is the C++ bool return (the C++ blocks). */
  askResignGame?(done: (fYes: boolean) => void): void;
  /** ggame.AskObserveGame (game.cpp:1917-1940) — the continue-as-observer
   *  modal; done(fYes) is the C++ bool return. */
  askObserveGame?(done: (fYes: boolean) => void): void;
  /** ggame.SaveReinitializeGame (game.cpp:1942-1964). */
  saveReinitializeGame?(): void;
  /** m_pchatter->ShowChat (MP chat, M5). */
  showChat?(): void;
  /** gpplrLocal->ShowObjectives (Player.cpp:1130 — ui/objectives.ts) — the
   *  modal objectives/summary; done(nGo) = the C++ return. */
  showObjectives?(
    nShowObjectives: number,
    fForceInfoDisplay?: boolean,
    fAborting?: boolean,
    done?: (nGo: number) => void,
  ): void;
  /** HTML/DOM UI shell plan (Phase 5, Form 2): render the in-game PAUSE MENU
   *  (kidfInGameMenu) as a DOM overlay. When set, DoInGameMenu
   *  branches here INSTEAD of ShellDoModal — it passes the visible buttons (the
   *  SP/MP descriptor, in order) and an onSelect that routes the chosen kidc
   *  through the SAME post-modal action handler (handleInGameMenuChoice). The SP
   *  pause is held by DoInGameMenu around it (MP is not paused). DISPLAY-only — no
   *  sim/command/wire change; the actions (gameOverEvent, save/load, sub-forms) are
   *  the shared handler's, unchanged. Absent (a headless harness) ⇒ no UI shown. */
  domInGameMenu?(buttons: InGameMenuButton[], onSelect: (idc: number) => void): void;
  /** HTML/DOM UI shell plan (Phase 5, Form 3): render the
   *  WIN / LOSE RESULTS SUMMARY (kidfWinSummary / kidfLoseSummary) as a DOM
   *  overlay. When set, ShowObjectives' tryAgain (for so !== ksoObjectives) renders
   *  this over the live canvas, zip-in, modal.
   *  The side-effects/SFX/flags (kfPlrSummaryShown, SetFlags, sendWinStats,
   *  markComplete) run in ShowObjectives BEFORE this branch.
   *  `onChoice(idc)` routes the chosen button through the SAME shared
   *  post-modal handler the canvas close used (→ the SAME gameOverEvent nGo).
   *  Absent (a headless harness) ⇒ no UI shown. DISPLAY-only — no sim/wire change. */
  domObjectivesSummary?(info: ObjectivesSummaryInfo, onChoice: (idc: number) => void): void;
  /** HTML/DOM UI shell plan (Phase 5, Form 4): render the
   *  in-game GAME OPTIONS form (kidfInGameOptions) as a DOM overlay. When set,
   *  DoModalGameOptionsForm branches here INSTEAD of LoadForm/ShellDoModal — the
   *  DOM view does the read/apply against THIS env directly (the SAME seams the
   *  canvas InGameOptionsForm used: gameSpeed/setGameSpeed, options, sndm, plrLocal,
   *  getSimUIForm, savePreferences) and calls `done` when it closes (OK or CANCEL).
   *  Presentation-only — no sim/command/wire change. Absent (a headless harness) ⇒
   *  no UI shown. The pause-menu OPTIONS → DoModalGameOptionsForm routes here. */
  domGameOptions?(done: () => void): void;
  /** HTML/DOM UI shell plan (Phase 5): render the in-game PAUSE-MENU
   *  HELP (handleInGameMenuChoice kidcHelp → the canvas Help() FULL manual, kidfHelpWide)
   *  as a DOM overlay. When set, the kidcHelp arm branches here INSTEAD of Help() — a
   *  modal full-manual overlay (the SAME mountHelpManual renderer the DOM main-menu HELP
   *  uses) over the paused canvas; `done` is the canvas Help()'s post-modal continuation
   *  (handleInGameMenuChoice's finish — un-pause SP + done). DISTINCT from the build
   *  panels' SECTION-only "?" (domHelp on BuildUIDeps): this is the WHOLE manual from the
   *  top. DISPLAY-only — no sim/command/wire change. Absent (a headless harness) ⇒ no UI shown. */
  domHelpManual?(done: () => void): void;
  /** HTML/DOM UI shell plan (Phase 5, Form 6): render the in-
   *  mission E-COM overlay (kidfEcomSmall / kidfEcomLarge) as a DOM overlay. When
   *  set, Ecom() branches here INSIDE its EnqueueStoryModal show callback (so it
   *  stays queued + SP-paused exactly like the canvas form did). The DOM typewriter is
   *  driven by a BROWSER timer (rAF) — NOT gtimm — so the text animates under the
   *  story-modal sim pause (fixing the "text stopped animating" regression; the
   *  canvas typewriter ran on gtimm, which freezes while paused). `done()` is the
   *  C++ post-modal return point — the host unmounts the overlay + releases the
   *  queue's next(). Absent (a headless harness) ⇒ no UI shown. DISPLAY-only. */
  domEcom?(info: EcomInfo, done: () => void): void;
  /** HTML/DOM UI shell plan (Phase 5 — kidfCutScene 1047): render the
   *  in-mission CUTSCENE player (the "opening screens" — image + typed
   *  narration with inline <img>/<snd> tags) as a DOM overlay. When set, CutScene()
   *  branches here INSIDE its EnqueueStoryModal show callback (so it stays queued +
   *  SP-paused exactly like the canvas form did). The DOM typewriter is browser-timer
   *  driven (rAF) so the narration animates under the story-modal sim pause (the SAME
   *  fix as domEcom; the canvas gtimm typewriter froze while paused). `done()` is
   *  the C++ post-modal return point — the host unmounts the overlay + releases the
   *  queue's next(). Absent (a headless harness) ⇒ no UI shown. DISPLAY-only. */
  domCutScene?(info: CutSceneInfo, done: () => void): void;
  /** HTML/DOM UI shell plan (Phase 5 — survey I10): render the
   *  HtMessageBox family (alerts / warnings / errors / Yes-No queries — kidfMessageBox
   *  1029 OK-only / kidfMessageBoxQuery 1058 Yes/No) as a DOM overlay. When set,
   *  HtMessageBox (ui/msgbox.ts) branches here INSTEAD of LoadForm/DoModal — it passes
   *  the title, the message, the variant ('ok'/'query') and the white-border flag, and an
   *  `done(fOk)` continuation. The DOM view reports the user's OK/Yes/No through done(),
   *  which the host routes to the SAME callback the canvas DoModal used (the resign/leave/
   *  exit/observe behaviour stays byte-identical — the view constructs NO Message and
   *  touches NO cmdq). The single seam covers EVERY HtMessageBox call site (~30). Absent
   *  (a headless harness) ⇒ no UI shown. DISPLAY-only — no sim/command/wire change. */
  domMessageBox?(
    title: string,
    message: string,
    variant: 'ok' | 'query',
    whiteBorder: boolean,
    done: (fOk: boolean) => void,
  ): void;
  /** HTML/DOM UI shell plan (Phase 5): render the in-game LOAD
   *  (kidfLoadGame 1038) / SAVE (kidfSaveGame 1039) slot-list forms as a DOM overlay.
   *  When set, PickLoadGameStream / PickSaveGameStream (loadsave.ts) branch here INSTEAD of
   *  LoadForm/ShellDoModal — passing the SaveStore (the HOST enumerates the slots via the
   *  SAME saves-io the shell LOAD SAVED GAME uses) and a `done(slot|null)` continuation
   *  (null = BACK/Cancel). The HOST opens the load stream / writes the save exactly as the
   *  canvas DoModal did (the view constructs no stream, touches no store). Covers BOTH Load
   *  entry points (pause-menu LOAD GAME + the LOSE-summary LOAD SAVED) via the one seam.
   *  Absent (a headless harness) ⇒ no UI shown. DISPLAY-only — no sim/command/wire change. */
  domLoadGame?(store: SaveStore, done: (slot: number | null) => void): void;
  domSaveGame?(store: SaveStore, done: (slot: number | null) => void): void;
  /** HTML/DOM UI shell plan (Phase 5): render the MP "WAITING FOR ALL
   *  PLAYERS…" sim-start overlay (WaitForm / kidfWaiting 1062) as a DOM overlay. Shown on
   *  the SHARED MP sim-start path (gamesession.ts) and torn down at the first block — so the
   *  hook MOUNTS the overlay and RETURNS its unmount closure (no result). Absent (a headless
   *  harness) ⇒ no UI shown. DISPLAY-only — no sim/command/wire change. */
  domWaiting?(title: string): () => void;
  /** HTML/DOM UI shell plan (Phase 5 — forms.ini-elim): the in-game MP "LAG ALERT"
   *  Yes/No query (SimUI.cpp OnLagNotify) — a DEDICATED, modeless DOM overlay seam (NOT the
   *  shared domMessageBox singleton; the lag box coexists with / outlives other boxes).
   *  CreateLagForm (ui/lagform.ts) branches here INSTEAD of CreateHtMessageBox over forms.ini.
   *  The box reports Yes/No through done(fOk) (the SAME CsKillLaggingPlayer vote — wire-
   *  identical) and returns its teardown closure. Absent (a headless harness) ⇒ no UI
   *  shown. DISPLAY-only — no sim/command/wire change. */
  domLagBox?(title: string, body: string, done: (fOk: boolean) => void): () => void;
  /** the display-mode seams (web default: one mode). */
  display?: DisplaySeam;
  /** ggame.GetVersionString hack input (Shell.cpp:541) — default gszVersion. */
  getVersionString?(): string;
  /** HostInitiateAsk (the MP forms' name/password entry): modeless native
   *  text-entry UI; when it completes the host posts askStringEvent and the
   *  form reads getAskString(). The embedder owns the surface (DOM prompt on
   *  the web; tests script it). */
  // C++ HostInitiateAsk(title, max, def, int keyboard = default, bool secure = false)
  // (ht.h:8931). The TS port collapsed the trailing pair into one optional flag, and the
  // two consumers fill it differently: the MP forms pass a boolean (the `secure` password
  // flag), the DLC form (DlcEnv) passes a keyboard-type number. The param is typed
  // `number | boolean` so a single declaration covers both and DlcEnv can extend ShellEnv
  // without an incompatible-override error. Type-only; no call site changes.
  initiateAsk?(
    pszTitle: string,
    cchMax: number,
    pszCurrent: string,
    fPassword?: number | boolean,
  ): void;
  /** HostGetAskString — the string the completed ask produced. */
  getAskString?(): string;
  /** WebAddBot (gameform.cpp:22-32 — the window.WI_BOT_CONTROL POST); the
   *  Add Bot button ships disabled, the seam is kept wired. */
  addBot?(roomid: number, gameid: number): void;
}

// game/Shell.cpp:566-568
export const kctZip = 25; // 250 ms
export const kctZipLag = 3; // 30 ms
export const kctRate = 2;

// re-export for the shell flow modules (game/missionlist.h:19-22 lives in io/)
export type { MissionIdentifier };
